import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SaveAltIcon from '@mui/icons-material/SaveAlt';
import { LOCATIONS, USER_CONTEXT } from '../../constants';
import { updateFormDefinition } from '../../util/APIUtils';
import { EVENTS } from '../services/constants';
import { eventEmitter } from '../services/event-emitter-service';
import store, { showToast } from '../core/store';
import {
  FORM_SUBMISSIONS_PAGE_SIZE,
  buildFormSubmissionsActionsDropdownOptions,
  buildFormSubmissionsColumns,
  buildFormSubmissionsExportInput,
  buildFormSubmissionsTdStyles,
  collectSortedDataKeysFromSubmissions,
  collectSubmissionFieldMetaFromDefinition,
  fetchFormSubmissionsPageData,
  getLocationFilterOptions,
  isFormIdPresent,
  mapFormSubmissionsToGridData,
  showFormSubmissionErrorToast,
} from './utils/form-submission.util';
import {
  exportFormSubmissionAsPdf,
  exportFormSubmissionsArchive,
} from './utils/form-export-pdf.util';
import {
  FEEDS_FORMS_TAB_ROUTE,
  FORM_RESPONSE_PDF_EXPORT_ERROR,
  FORM_SUBMISSIONS_LOAD_ERROR,
  FORM_SUBMISSIONS_ZIP_EXPORT_ERROR,
} from './constants/form-submission.constants';
import { FORMS_UPDATE_ERROR, formEditRoute } from './constants/forms.constants';
import { mapApiFormDefinitionToForm } from './utils/form.util';

export const useFormSubmissionsHook = () => {
  const navigate = useNavigate();
  const { formId } = useParams();
  const currentPersonId = USER_CONTEXT?.personId;
  const [formTitle, setFormTitle] = useState('');
  const [formDefinition, setFormDefinition] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  /** Bumps when global `LOCATIONS` is populated so location filter options stay in sync. */
  const [locationFilterRevision, setLocationFilterRevision] = useState(0);
  const [previewForm, setPreviewForm] = useState(null);

  const bumpLocationFilterRevision = useCallback(() => {
    setLocationFilterRevision((n) => n + 1);
  }, []);

  const resetSubmissionsState = useCallback(() => {
    setFormDefinition(null);
    setSubmissions([]);
    setLoading(false);
  }, []);

  const handleLoadFailure = useCallback(() => {
    setFormTitle('');
    setFormDefinition(null);
    setSubmissions([]);
    store.dispatch(showToast(FORM_SUBMISSIONS_LOAD_ERROR));
  }, []);

  const fetchSubmissionsForForm = useCallback(async () => {
    const pageData = await fetchFormSubmissionsPageData(formId);
    setFormTitle(pageData.formTitle);
    setFormDefinition(pageData.formDefinition);
    setSubmissions(pageData.submissions);
    bumpLocationFilterRevision();
  }, [formId, bumpLocationFilterRevision]);

  const load = useCallback(async () => {
    if (!isFormIdPresent(formId)) {
      resetSubmissionsState();
      return;
    }
    setLoading(true);
    try {
      await fetchSubmissionsForForm();
    } catch {
      handleLoadFailure();
    } finally {
      setLoading(false);
    }
  }, [formId, resetSubmissionsState, fetchSubmissionsForForm, handleLoadFailure]);

  const dataKeys = useMemo(
    () => collectSortedDataKeysFromSubmissions(submissions),
    [submissions],
  );

  const fieldMeta = useMemo(
    () => collectSubmissionFieldMetaFromDefinition(formDefinition),
    [formDefinition],
  );

  const { signatureKeys, currencyMeta, selectboxKeys, surveyKeys, datetimeKeys } = fieldMeta;

  const handleExportSubmissions = useCallback(async () => {
    const exportInput = buildFormSubmissionsExportInput({
      submissions,
      formDefinition,
      formTitle,
    });
    try {
      await exportFormSubmissionsArchive(exportInput);
    } catch {
      showFormSubmissionErrorToast(FORM_SUBMISSIONS_ZIP_EXPORT_ERROR);
    }
  }, [submissions, formDefinition, formTitle]);

  const handleExportResponseToPdf = useCallback(
    async (submission) => {
      try {
        await exportFormSubmissionAsPdf({ submission, formDefinition, formTitle });
      } catch {
        showFormSubmissionErrorToast(FORM_RESPONSE_PDF_EXPORT_ERROR);
      }
    },
    [formDefinition, formTitle],
  );

  const submissionActionHandlers = useMemo(
    () => ({
      onExportPdf: handleExportResponseToPdf,
    }),
    [handleExportResponseToPdf],
  );

  const locationFilterOptions = useMemo(
    () => getLocationFilterOptions(LOCATIONS),
    // LOCATIONS is module-level; `locationFilterRevision` bumps when it is populated (event / load).
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional invalidation, not a missing dep on LOCATIONS
    [locationFilterRevision],
  );

  const columns = useMemo(
    () => buildFormSubmissionsColumns(submissions, signatureKeys, locationFilterOptions),
    [submissions, signatureKeys, locationFilterOptions],
  );

  const gridData = useMemo(
    () =>
      mapFormSubmissionsToGridData(submissions, {
        currencyMeta,
        dataKeys,
        datetimeKeys,
        selectboxKeys,
        signatureKeys,
        submissionActionHandlers,
        surveyKeys,
      }),
    [
      submissions,
      dataKeys,
      signatureKeys,
      currencyMeta,
      selectboxKeys,
      surveyKeys,
      datetimeKeys,
      submissionActionHandlers,
    ],
  );

  const tdStyles = useMemo(() => buildFormSubmissionsTdStyles(columns), [columns]);

  const handleBackToForms = useCallback(() => {
    navigate(FEEDS_FORMS_TAB_ROUTE);
  }, [navigate]);

  const formForActions = useMemo(() => mapApiFormDefinitionToForm(formDefinition), [formDefinition]);

  const clearPreviewForm = useCallback(() => {
    setPreviewForm(null);
  }, []);

  const handlePreviewForm = useCallback((form) => {
    setPreviewForm(form);
  }, []);

  const handleEditFormFromMenu = useCallback(
    (form) => {
      if (form?.id == null || form.id === '') {
        return;
      }
      navigate(formEditRoute(form.id));
    },
    [navigate],
  );

  const handleToggleActiveFromMenu = useCallback(
    async (form) => {
      setLoading(true);
      try {
        await updateFormDefinition(form.id, { is_active: !form.active });
        await fetchSubmissionsForForm();
      } catch {
        store.dispatch(showToast(FORMS_UPDATE_ERROR));
      } finally {
        setLoading(false);
      }
    },
    [fetchSubmissionsForForm],
  );

  const formActionsDropdownOptions = useMemo(
    () =>
      buildFormSubmissionsActionsDropdownOptions(
        formForActions,
        {
          onPreview: handlePreviewForm,
          onEdit: handleEditFormFromMenu,
          onToggleActive: handleToggleActiveFromMenu,
        },
        currentPersonId,
      ),
    [
      formForActions,
      handlePreviewForm,
      handleEditFormFromMenu,
      handleToggleActiveFromMenu,
      currentPersonId,
    ],
  );

  const primaryButtonProps = useMemo(
    () => ({
      text: 'Actions',
      endIcon: <ArrowDropDownIcon />,
      dropdownOptions: formActionsDropdownOptions,
      show: formActionsDropdownOptions.length > 0,
    }),
    [formActionsDropdownOptions],
  );

  const secondaryButtonProps = useMemo(
    () => ({
      text: 'Export CSV',
      variant: 'outlined',
      startIcon: <SaveAltIcon />,
      onClick: handleExportSubmissions,
      disabled: loading || submissions.length === 0,
    }),
    [handleExportSubmissions, loading, submissions.length],
  );

  const subscribeToLocationsFetched = useCallback(() => {
    eventEmitter.on(EVENTS.LOCATIONS_FETCHED, bumpLocationFilterRevision);
    return () => {
      eventEmitter.off(EVENTS.LOCATIONS_FETCHED, bumpLocationFilterRevision);
    };
  }, [bumpLocationFilterRevision]);

  useEffect(subscribeToLocationsFetched, [subscribeToLocationsFetched]);
  useEffect(() => {
    load();
  }, [load]);

  return {
    formId,
    formTitle,
    formForActions,
    currentPersonId,
    columns,
    gridData,
    tdStyles,
    pageSize: FORM_SUBMISSIONS_PAGE_SIZE,
    loading,
    refreshSubmissions: load,
    handleBackToForms,
    primaryButtonProps,
    secondaryButtonProps,
    previewForm,
    clearPreviewForm,
    handlePreviewForm,
    handleEditFormFromMenu,
    handleToggleActiveFromMenu,
  };
};
